import { useCallback, useReducer } from "react";
import { BundleTypes, DISCOUNT_TYPES, generateRandomUniqueId, generateUniqueId } from "../../constants";
import {
    Action,
    BundleBuilderConfigInterface,
    ChangeEvents,
    FieldsType,
    FixedBoxConfig,
    ProductConditions,
    StepsType,
    SubStepType,
} from "../../Types";

export const DEFAULT_FIXED_BOX: FixedBoxConfig = { itemsCount: 2, fixedPrice: 50, boxName: "Box of 2" };

export const DEFAULT_PRODUCT_CONDITION: ProductConditions = {
    included_products: [],
    hidden_products: [],
    hidden_steps: [],
    hidden_substeps: [],
    action_type: "products",
};

export const DEFAULT_FIELD: FieldsType = {
    field_type: "input",
    field_title: "Field 1",
    field_placeholder: "Message",
    is_required: false,
    dropdown_options: [],
    default_value: "",
    file_upload_settings: {
        max_file_size: 5,
        allow_multiple: false,
        allowed_file_types: ["images"],
    },
};

export const DEFAULT_SUB_STEP: SubStepType = {
    step_label: "Category 1",
    product_selection: "specific",
    products: [],
    collections: [],
    subStep_condition_basis: "items_count",
    subStep_condition_type: "min_products",
    product_count_limit: 1,
    step_error_msg: "{category_label}: Select at least {productCount} products",
    exempt_product_ids: [],
    allow_product_custom_text: false,
    product_custom_text_label: "Custom Text",
    product_custom_text_placeholder: "e.g. Add your name",
    hidden: false,
};

export const DEFAULT_STEP: StepsType = {
    step_label: "Step 1",
    page_title: "",
    page_subtitle: "",
    bottom_subtitle: "",
    header_subtitle: "",
    step_icon: null,
    step_content_type: "products",
    product_selection: "specific",
    products: [],
    collections: [],
    enable_subSteps: false,
    subSteps: [],
    step_conditions: [
        {
            basis: "items_count",
            type: "max_products",
            limit: 10,
            msg: "Select maximum {productCount} products",
        },
    ],
    substep_rules_type: "substep_level",
    fields: [],
    render_as_variants: false,
    show_step_conditions: false,
    show_banner: false,
    banner_desktop: "",
    banner_mobile: "",
    allow_product_custom_text: false,
    product_custom_text_label: "Custom Text",
    product_custom_text_placeholder: "e.g. Add your name",
    exempt_product_ids: [],
    enable_capacity_progress_bar: false,
    merge_total_capacity: true,
    enable_dosage_calculation: false,
};

export const initialConfigData: BundleBuilderConfigInterface = {
    status: false,
    bundle_type: BundleTypes.BUNDLE_BUILDER,
    is_paused: false,
    form_id: "",
    bundle_as_product: {
        product_title: "",
        product_description: "",
        images: [],
        show_on_product_page: [],
        product_handle: "",
        variant_id: "",
        double_discount: false,
        collections: [],
    },
    isProductPageVisible: false,
    allow_pre_selected_products: false,
    pre_selected_products: [],
    make_pre_selected_compulsory: true,
    product_card_desktop_size: "large",
    product_card_mobile_size: "medium",
    secondary_button_type: "edit_bundle",
    allow_conditional_logic: false,
    allow_quantity_input: false,
    product_conditions: [],
    redirect_options: "checkout",
    show_popup_on_cart_redirect: false,
    popup_text: "<p>Yay, your bundle has been added to your cart.</p>",
    popup_redirect_url: "",
    hide_bundle_header: false,
    show_progress_bar: false,
    footer_layout: "bottom",
    // @ts-expect-error => keeping it empty bydefault for template selection at start
    selected_template_bb: "",
    // design from
    bundles_design: {
        default_cta: true,
        bundle_title_size: 15,
        bundle_title_color: "#000000",
        bundle_title_style: "650 normal",
        product_name_size: 14,
        product_name_color: "#050505",
        product_name_style: "450 normal",
        product_price_size: 12,
        product_price_color: "#9D9D9D",
        product_price_style: "350 normal",
        // border_color: "#050505",
        border_width: 0,
        border_radius: 8,
        // background_color: "#F2F4F2",
        // highlighted_offer_tag_size: 12,
        // highlighted_offer_tag_color: "#F8F8F8",
        // highlighted_offer_tag_style: "550 italic",
        highlighted_price_tag_size: 14,
        highlighted_price_tag_color: "#000000",
        highlighted_price_tag_style: "550 normal",
        total_footer_text_size: 14,
        total_footer_text_color: "#FFFFFF",
        total_footer_price_color: "#FFFFFF",
        total_footer_strike_off_price_color: "#9D9D9D",
        total_footer_discount_color: "#FFFFFF",
        total_footer_text_style: "550 normal",
        // total_footer_price_text_size: 16,
        // total_footer_price_text_color: "#000000",
        // total_footer_price_text_style: "750 normal",
        total_footer_background_color: "#303030",
        // total_footer_background_style: "450 normal",
        product_background_color: "#FFFFFF",
        product_border_color: "#F2F2F2",
        add_to_cart_bg_color: "#303030",
        add_to_cart_border_radius: 8,
        // add_to_cart_border_color: "#FFFFFF",
        add_to_cart_text_color: "#F5F5F5",
        add_to_cart_text_size: 14,
        add_to_cart_text_style: "750 normal",
        // add_to_cart_border_width: 0,
        bundle_widget_bg: "#FFFFFF",
        bundle_widget_font_family: "Custom Store Font",
        product_img_size: "medium",
        product_custom_img_size: 160,
        product_custom_img_size_mobile: 160,
        product_img_show: "all",
        is_product_img_height_auto: false,
        // product_img_border_radius: 10,
        // free_gift_header_bg_color: "#E3E3E3",
        // free_gift_header_color: "#303030",
        // free_gift_header_size: 13,
        // free_gift_header_style: "650 normal",
        // free_gift_bg_color: "#E0E0E0",
        // free_gift_text_color: "#303030",
        // free_gift_text_size: 12,
        // free_gift_text_style: "650 normal",
        // free_gift_border: true,
        // free_gift_border_width: 0,
        // free_gift_border_color: "#441144",
        fixed_boxes_bg_color: "#FFFFFF",
        fixed_boxes_selected_bg_color: "#000000",
        fixed_boxes_text_color: "#000000",
        fixed_boxes_selected_text_color: "#FFFFFF",
        fixed_boxes_text_size: 12,
        // product_img_border_width: 1,
        // product_img_border_color: "#CCCCCC",
        variant_selector_text_size: 12,
        variant_selector_text_color: "#383838",
        variant_selector_text_style: "450 normal",
        secondary_cta_bg_color: "#FFFFFF",
        secondary_cta_text_color: "#000000",
        tertiary_cta_bg_color: "#ffffff28",
        tertiary_cta_text_color: "#E9E9E9",
        review_product_border_color: "#F1F1F1",
        review_product_border_width: 1,
        review_product_border_radius: 8,
        review_product_img_border_color: "#F1F1F1",
        review_product_img_border_radius: 8,
        review_product_img_border_width: 1,
        step_label_color: "#3F3F3F",
        step_page_title_color: "#000000",
        step_page_title_size: 16,
        step_page_title_style: "750 normal",
        step_page_subtitle_color: "#3F3F3F",
        step_page_subtitle_size: 13,
        step_page_subtitle_style: "450 normal",
        order_summary_header_text_color: "#222222",
        order_summary_header_text_size: 15,
        order_summary_header_text_style: "550 normal",
        order_summary_rows_text_color: "#000000b2",
        order_summary_rows_text_size: 12,
        order_summary_rows_text_style: "450 normal",
        order_summary_bg_color: "#EDEDED",
        edit_bundle_cta_bg_color: "#afacac3b",
        edit_bundle_cta_text_color: "#424242",
        edit_bundle_cta_style: "450 normal",
        edit_bundle_cta_size: 14,
        bb_margin_top: 0,
        bb_margin_bottom: 10,
        product_card_title_alignment: "left",
        substep_bg_color: "#303030",
        substep_text_color: "#ffffff",
        substep_header_text_size: 14,
        mobile_substep_header_layout: "dropdown",
        // popup_cta_bg_color: "#030303",
        popup_bg_color: "#FFFFFF",
        popup_text_color: "#303030",
        progress_bar_color: "#00FFD1",
        progress_bar_bg_color: "#484545",
        step_circle_bg_color: "#000000",
        addon_title_text_color: "#424242",
        addon_title_text_size: 14,
        addon_card_bg_color: "#f5f5f5",
        step_circle_color: "#000000",
    },
    bundle_name: "",
    title: "Build Your Own Box",
    description: "",
    top_html: "",
    discount_type: "percentage",
    tier_discount_basis: "items_count",
    discount_value: 5,
    hide_prices_product_card: false,
    fixed_boxes: [],
    fixed_boxes_upsell_message: "Add {noOfRemainingProducts} more product(s) to unlock {boxName} for {price}",
    fixed_boxes_success_message: "Congratulations! You've got the best box offer.",
    products: [],
    steps: [{ ...DEFAULT_STEP }],
    review_step: {
        step_label: "Review Your Box",
        page_title: "You've made the right choice!",
        is_enabled: true,
        step_icon: null,
        enable_addon_products: false,
        addon_section_title: "Complete your box",
        addon_product_discount_label: "Add-ons discount",
        addon_product_selection: "specific",
        addon_button_text: "Add",
        addon_products: [],
        addon_products_total_lable: "Add-ons",
        collections: [],
        apply_discount_on_addon_products: false,
        discount_type: "percentage",
        discount_value: 10,
    },
    default_step: { ...DEFAULT_STEP },
    bundle_product_level: "product",
    hide_native_elements: "",
    add_to_cart_cta_text: "Add to Cart",
    quantity_text: "Qty",
    footer_total_text: "Total",
    footer_text: "Off",
    free_text: "Free",
    item_added_text: "{itemCount} Item(s) Added",
    buy_now_cta_text: "Add to Bundle",
    edit_cta_text: "Edit Bundle",
    start_again_cta_text: "Start Again",
    bundle_ready_text: "Your {title} Bundle is ready!",
    next_cta_text: "Next",
    previous_cta_text: "Previous",
    checkout_cta_text: "Go to Checkout",
    checkout_continue_shopping_cta_text: "Continue Shopping",
    checkout_view_cart_cta_text: "View Cart",
    checkout_box_label: "Box",
    checkout_discount_label: "{discount} off",
    show_more_text: "Show more",
    show_less_text: "Show less",
    out_of_stock_text: "Out of Stock!",
    order_summary_title: "Order Summary",
    order_summary_subtotal: "Subtotal",
    order_summary_discount: "Discount",
    no_products_selected_text: "No Product(s) Selected",
    file_upload_uploading_text: "Uploading...",
    file_upload_click_to_upload_multiple_text: "Click to upload multiple files",
    file_upload_click_to_upload_single_text: "Click to upload a file",
    file_upload_max_size_text: "Max size: {maxSize}",
    file_upload_already_uploaded_error: "File already uploaded",
    file_upload_only_one_file_error: "Only one file is allowed",
    start_date: new Date().toISOString(),
    end_date: null,
    use_compare_at_price: false,
    show_product_price_with_compare_price: false,
    apply_discount_on_compare_price: false,
    allow_free_gifts: false,
    free_gifts: [],
    free_gift_text: "+ Free Gift",
    bundle_embed_id: generateRandomUniqueId(),
    show_swatches: false,
    swatchOptions: [],
    swatchesProductsMap: {},
    swatchShape: "circle",
    swatchSize: 18,
    offer_cta_text: "Build Your Box",
    limit_show_offer_cta: "none",
    show_offer_cta_pages: [],
    offer_cta_position: "below_cart",
    bundle_builder_link: "",
    offer_name: "",
    b2b_discounts_enabled: false,
    b2b_discount_type: "percentage",
    b2b_discount_value: 10,
    discount_options: [
        {
            id: generateUniqueId(),
            discountType: DISCOUNT_TYPES.FIXED,
            discountValue: 10,
            quantity: 2,
            maxQuantity: 2,
            addRange: false,
            ShowTierSuccessMessage: true,
            TierSuccessMessage:
                "Add {noOfRemainingProducts} more product(s) to unlock the fixed price offer of {discount}!",
            showFinalSuccessMessage: true,
            finalSuccessMessage: "Congratulations! You've got the best offer on your bundle.",
            selected_products: [],
        },
        {
            id: generateUniqueId(),
            discountType: DISCOUNT_TYPES.FIXED,
            discountValue: 20,
            quantity: 3,
            maxQuantity: 3,
            addRange: false,
            ShowTierSuccessMessage: true,
            TierSuccessMessage:
                "Add {noOfRemainingProducts} more product(s) to unlock the fixed price offer of {discount}!",
            showFinalSuccessMessage: true,
            finalSuccessMessage: "Congratulations! You've got the best offer on your bundle.",
            selected_products: [],
        },
    ],
    // target from
    targeting: {
        includeCustomers: "all",
        excludeCustomers: "none",
        includeCustomerData: "",
        excludeCustomerData: "",
        shopifyMarketsTargeting: "all",
        includeLocations: [],
        excludeLocations: [],
        shopifyMarketsTargetingData: [],
        //newKeys
        targetingType: "all",
        customerTagsEnabled: false,
        customerTagType: "include",
        customerTags: "",
        customerSpentEnabled: false,
        customerSpentType: "greater_than",
        customerSpent: "",
        customerCountriesEnabled: false,
        customerCountryType: "include",
        customerCountries: [],
    },
    hide_out_of_stock_products: false,
    show_search_bar: false,
    search_bar_alignment_desktop: "flex-end",
    search_bar_alignment_mobile: "center",
    auto_advance_when_condition_matched: false,
    hide_product_info_modal: false,
    disable_product_title_link: false,
    limit_max_quantity_per_product: false,
    max_quantity_per_product: 5,
    empty_card_footer_text: "Add Product(s)",
    choose_option_text: "Choose Option",
    disable_cta_when_stock_reached: false,
    show_product_grid_view: false,
    show_broken_quantity_in_grid: false,

    // Settings
    general_settings: {
        show_bundle_on_product_page: false,
        show_multiple_bundles_on_product_page: false,
        redirect_after_adding_product_to_cart: "",
        redirect_to_checkout: false,
        show_powered_by_kefi: true,
        max_width: 1000,
        bundle_alignment: "flex-start",
        override_max_width: false,
        override_margin_top: false,
        margin_top: 0,
        show_carousel: false,
        auto_rotate_carousel: false,
        carousel_interval: "5",
    },
    bundles_settings: {
        enable_stock_control: false,
        out_of_stock_text: "Out of stock",
        listing_variant_with_image: false,
        work_only_with_widget: false,
        bundle_position: "below_cart",
        custom_selector: "",
        custom_css: "",
        custom_js: "",
    },
    show_bundle_widget: "show_everywhere",
    free_shipping: false,
    animation_add_to_cart_btn: "none",
    order_discounts: true,
    product_discounts: true,
    shipping_discounts: true,
    allow_exclude_from_step_conditions: false,
    substep_layout: "tabs",
    show_total_quantity_in_footer: true,
    ab_config: {
        enable: 0,
        exp_id: generateRandomUniqueId(),
        variants: [],
        traffic_allocation_type: "even",
        traffic_percentage: {},
    },
};

const useStandardHook = () => {
    const bundleConfigReducer = useCallback(
        (
            state: BundleBuilderConfigInterface,
            action: Action<BundleBuilderConfigInterface>,
        ): BundleBuilderConfigInterface => {
            switch (action?.type) {
                case ChangeEvents.initState:
                    return { ...state, ...action.init };
                default:
                    if (action?.key && action.isComplex) {
                        return {
                            ...state,
                            [action?.key]: {
                                ...(state[action.key] as object),
                                ...(action.payload as object),
                            },
                        };
                    } else if (action?.key) {
                        return { ...state, [action?.key]: action.payload };
                    } else return { ...state };
            }
        },
        [],
    );

    const [bundleConfig, dispatchBundleConfig] = useReducer(bundleConfigReducer, initialConfigData);

    return {
        bundleConfig,
        dispatchBundleConfig,
    };
};

export default useStandardHook;
